<template>
  <div v-if="isVisualizeMode" class="enemy-info-card" :class="{ expanded: examine.expanded }">
    <!-- Hint when no enemy selected -->
    <div v-if="!examine.examinedEnemyId" class="info-hint">
      <ion-icon :icon="eyeOutline" class="hint-icon" />
      <span>Tap any creature to examine it</span>
    </div>

    <!-- Enemy info when selected -->
    <template v-else>
      <div class="info-summary" @click="toggleExpanded">
        <span class="enemy-name">{{ examine.enemyName }}</span>
        <span class="summary-text">{{ summaryText }}</span>
        <ion-icon :icon="examine.expanded ? chevronUp : chevronDown" class="expand-icon" />
      </div>
      <div v-if="examine.expanded" class="stat-sheet">
        <div v-for="(info, i) in examine.displayInfos" :key="i" class="ability-row">
          <HexBadge :icon="info.icon" :label="info.label" />
          <div class="ability-details">
            <div class="ability-label">{{ info.label }}</div>
            <div class="ability-desc">{{ info.description }}</div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, inject } from 'vue';
import { chevronUp, chevronDown, eyeOutline } from 'ionicons/icons';
import { IonIcon } from '@ionic/vue';
import { GameEvents } from '@/lib/GameEvents';
import HexBadge from './HexBadge.vue';

const game = inject('game') as any;
const examine = computed(() => game.state.examine);

const isVisualizeMode = computed(() => game.state.gameplay.currentActionMode === 'VISUALIZE');

const summaryText = computed(() => {
  const infos = examine.value.displayInfos;
  if (!infos || infos.length === 0) return '';
  return infos.map((i: any) => i.label).join(' · ');
});

const toggleExpanded = () => {
  game.emit(GameEvents.ENEMY_STAT_SHEET_TOGGLED);
};
</script>

<style scoped>
.enemy-info-card {
  position: fixed;
  top: calc(var(--ion-safe-area-top, 0px) + 60px);
  left: 50%;
  transform: translateX(-50%);
  max-width: 300px;
  width: calc(100% - 24px);
  z-index: 999;
  background: rgba(42, 24, 16, 0.96);
  color: var(--hw-text);
  border: 2px solid var(--hw-accent);
  border-radius: var(--hw-radius);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.5), 0 0 8px var(--hw-accent-glow);
  overflow: hidden;
}

.info-hint {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 12px;
  color: var(--hw-text-muted);
  font-size: 0.9em;
}

.hint-icon {
  font-size: 18px;
}

.info-summary {
  display: flex;
  align-items: center;
  padding: 10px 14px;
  gap: 8px;
  cursor: pointer;
}

.enemy-name {
  font-weight: bold;
  font-size: 1em;
}

.summary-text {
  flex: 1;
  font-size: 0.85em;
  color: var(--hw-text-muted);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.expand-icon {
  font-size: 18px;
  color: var(--hw-text-muted);
}

.stat-sheet {
  padding: 4px 14px 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.ability-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.ability-details {
  flex: 1;
}

.ability-label {
  font-weight: bold;
  font-size: 0.9em;
}

.ability-desc {
  font-size: 0.8em;
  color: var(--hw-text-muted);
}
</style>
